/**
 * Palais product page interactivity.
 * - Thumbnail click -> swap main gallery image
 * - Gallery prev/next arrows -> slide transition (directional) + circular navigation
 * - Variant pill click -> highlight, update price/SKU/hidden id, swap image if variant has one
 * - Qty +/- buttons
 * - AJAX add-to-cart -> dispatches cart:update for the cart drawer
 * - Favorites heart under gallery (window.AicoWishlist / pdt-favorites.js)
 * - Recommended products carousel (4 per page, prev/next + dots when > 4)
 */
(function () {
  'use strict';

  function getRecoCarouselPage(section) {
    const carousel = section.querySelector('[data-pp-reco-carousel]');
    if (!carousel) return 0;
    const n = parseInt(carousel.dataset.ppRecoPage, 10);
    return Number.isNaN(n) ? 0 : n;
  }

  function setRecoCarouselPage(section, index) {
    const carousel = section.querySelector('[data-pp-reco-carousel]');
    const track = section.querySelector('[data-pp-reco-track]');
    const prevBtn = section.querySelector('[data-pp-reco-carousel-prev]');
    const nextBtn = section.querySelector('[data-pp-reco-carousel-next]');
    const dotsRoot = section.querySelector('[data-pp-reco-dots]');
    if (!carousel || !track || !prevBtn || !nextBtn) return;
    const pages = track.querySelectorAll('.pp__reco-page');
    const totalPages = pages.length;
    if (totalPages === 0) return;
    const idx = Math.max(0, Math.min(index, totalPages - 1));
    carousel.dataset.ppRecoPage = String(idx);
    track.style.transform = `translateX(-${idx * 100}%)`;
    prevBtn.disabled = idx <= 0;
    nextBtn.disabled = idx >= totalPages - 1;
    if (dotsRoot) {
      dotsRoot.querySelectorAll('[data-pp-reco-carousel-dot]').forEach((dot, i) => {
        const on = i === idx;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }
  }

  function setupRecoCarousel(section) {
    const carousel = section.querySelector('[data-pp-reco-carousel]');
    const controls = section.querySelector('[data-pp-reco-carousel-controls]');
    const prevBtn = section.querySelector('[data-pp-reco-carousel-prev]');
    const nextBtn = section.querySelector('[data-pp-reco-carousel-next]');
    const track = section.querySelector('[data-pp-reco-track]');
    const dotsRoot = section.querySelector('[data-pp-reco-dots]');
    if (!carousel || !track || !prevBtn || !nextBtn || !controls) return;

    const pages = track.querySelectorAll('.pp__reco-page');
    const totalPages = pages.length;

    if (totalPages <= 1) {
      controls.hidden = true;
      if (dotsRoot) {
        dotsRoot.hidden = true;
        dotsRoot.innerHTML = '';
      }
      track.style.transform = '';
      carousel.dataset.ppRecoPage = '0';
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }

    controls.hidden = false;
    if (dotsRoot) {
      dotsRoot.hidden = false;
      dotsRoot.innerHTML = '';
      for (let i = 0; i < totalPages; i++) {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pp__reco-dot';
        btn.setAttribute('data-pp-reco-carousel-dot', String(i));
        btn.setAttribute('aria-label', `Seite ${i + 1} von ${totalPages}`);
        li.appendChild(btn);
        dotsRoot.appendChild(li);
      }
    }

    const saved = parseInt(carousel.dataset.ppRecoPage || '0', 10);
    const startIdx = Number.isNaN(saved) ? 0 : Math.min(saved, totalPages - 1);
    setRecoCarouselPage(section, startIdx);
  }

  function onRecoCarouselClick(e) {
    const section = e.currentTarget;
    const prev = e.target.closest('[data-pp-reco-carousel-prev]');
    if (prev && section.contains(prev) && !prev.disabled) {
      e.preventDefault();
      setRecoCarouselPage(section, getRecoCarouselPage(section) - 1);
      return;
    }
    const next = e.target.closest('[data-pp-reco-carousel-next]');
    if (next && section.contains(next) && !next.disabled) {
      e.preventDefault();
      setRecoCarouselPage(section, getRecoCarouselPage(section) + 1);
      return;
    }
    const dot = e.target.closest('[data-pp-reco-carousel-dot]');
    if (dot && section.contains(dot)) {
      e.preventDefault();
      const idx = parseInt(dot.getAttribute('data-pp-reco-carousel-dot'), 10);
      if (!Number.isNaN(idx)) setRecoCarouselPage(section, idx);
    }
  }

  function init(section) {
    const form = section.querySelector('[data-pp-form]');
    const mainImage = section.querySelector('[data-pp-main-image]');
    const gallerySlide = section.querySelector('[data-pp-gallery-slide]');
    const galleryTrack = section.querySelector('[data-pp-gallery-track]');
    const galleryImageBuff = section.querySelector('[data-pp-gallery-image-buff]');
    const mainBtn = section.querySelector('[data-pp-gallery-main]');
    const variantsRoot = section.querySelector('[data-pp-variants]');
    const priceEl = section.querySelector('[data-pp-price]');
    const unitPriceEl = section.querySelector('[data-pp-unit-price]');
    const variantIdInput = section.querySelector('[data-pp-variant-id]');
    const addBtn = section.querySelector('[data-pp-add]');
    const addLabel = section.querySelector('[data-pp-add-label]');
    const qtyInput = section.querySelector('[data-pp-qty-input]');
    // Gather all gallery images from the dot buttons (they carry data-src)
    const dots = Array.from(section.querySelectorAll('[data-pp-thumb]'));
    const images = dots.map((d) => ({
      src: d.getAttribute('data-src'),
      alt: d.getAttribute('data-alt') || ''
    }));
    let currentIndex = 0;
    let galleryAnimating = false;

    function applyGalleryImage(el, img) {
      if (!el || !img?.src) return;
      el.src = img.src;
      el.srcset = '';
      el.alt = img.alt;
    }

    function updateGalleryDots() {
      dots.forEach((d, idx) => {
        const active = idx === currentIndex;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    function setActiveIndex(i, options = {}) {
      if (!images.length) return;
      const nextIndex = Math.max(0, Math.min(images.length - 1, i));
      if (nextIndex === currentIndex) return;

      const direction =
        options.direction === 1 || options.direction === -1 ? options.direction : 0;
      const canAnimate =
        options.animate &&
        direction !== 0 &&
        gallerySlide &&
        galleryTrack &&
        galleryImageBuff &&
        mainImage &&
        !galleryAnimating &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (canAnimate) {
        runGallerySlide(nextIndex, direction);
        return;
      }

      if (galleryAnimating) {
        galleryAnimating = false;
        resetGalleryTrack();
      }

      currentIndex = nextIndex;
      applyGalleryImage(mainImage, images[currentIndex]);
      updateGalleryDots();
    }

    function resetGalleryTrack() {
      if (!galleryTrack) return;
      galleryTrack.style.transition = 'none';
      galleryTrack.style.transform = '';
      galleryTrack.offsetHeight;
      galleryTrack.style.transition = '';
      if (galleryImageBuff) {
        galleryImageBuff.removeAttribute('src');
        galleryImageBuff.setAttribute('aria-hidden', 'true');
      }
      if (mainImage) mainImage.removeAttribute('aria-hidden');
    }

    function runGallerySlide(nextIndex, direction) {
      galleryAnimating = true;
      const img = images[nextIndex];

      // Two-half track: left = mainImage, right = buff. We always lay the two
      // images out so the CURRENTLY visible image and the incoming image sit in
      // the two halves, pin the track to whichever half currently shows, then
      // animate to the other half.
      //   next (dir 1):  current on left, incoming on right, 0    -> -50%
      //   prev (dir -1): incoming on left, current on right, -50% ->   0
      const startPct = direction === 1 ? 0 : -50;
      const endPct = direction === 1 ? -50 : 0;

      if (direction === 1) {
        applyGalleryImage(mainImage, images[currentIndex]);
        applyGalleryImage(galleryImageBuff, img);
      } else {
        applyGalleryImage(mainImage, img);
        applyGalleryImage(galleryImageBuff, images[currentIndex]);
      }
      galleryImageBuff.removeAttribute('aria-hidden');

      let slideFinished = false;
      const onDone = (e) => {
        if (e && e.target !== galleryTrack) return;
        if (slideFinished) return;
        slideFinished = true;
        applyGalleryImage(mainImage, img);
        resetGalleryTrack();
        currentIndex = nextIndex;
        updateGalleryDots();
        galleryAnimating = false;
      };
      galleryTrack.addEventListener('transitionend', onDone);

      const beginSlide = () => {
        // Pin the start position with the transition disabled and FLUSH it with
        // a computed-style read (not just offsetHeight) so the browser records
        // this exact matrix as the transition's start — needed because for
        // "next" the start (0%) equals the resting transform, which otherwise
        // produces no baseline change and makes the slide skip/flash.
        galleryTrack.style.transition = 'none';
        galleryTrack.style.transform = 'translateX(' + startPct + '%)';
        void getComputedStyle(galleryTrack).transform;

        // Next frame: re-enable the transition and move to the end position.
        requestAnimationFrame(() => {
          galleryTrack.style.transition = '';
          galleryTrack.style.transform = 'translateX(' + endPct + '%)';
          window.setTimeout(() => onDone(), 500);
        });
      };

      // The incoming (off-screen) image must be decoded before it slides in,
      // otherwise it paints blank/late. On "next" it lives in the buffer, which
      // the previous reset emptied, so it always decodes from scratch.
      const incomingEl = direction === 1 ? galleryImageBuff : mainImage;
      if (incomingEl && incomingEl.decode) {
        incomingEl.decode().then(beginSlide).catch(beginSlide);
      } else {
        beginSlide();
      }
    }

    dots.forEach((dot, idx) => {
      dot.addEventListener('click', () => {
        if (idx === currentIndex) return;
        const direction = idx > currentIndex ? 1 : -1;
        setActiveIndex(idx, { animate: true, direction });
      });
    });

    const galleryPrevBtn = section.querySelector('[data-pp-gallery-prev]');
    const galleryNextBtn = section.querySelector('[data-pp-gallery-next]');

    function galleryStep(delta) {
      if (images.length <= 1) return;
      const len = images.length;
      const idx = ((currentIndex + delta) % len + len) % len;
      setActiveIndex(idx, { animate: true, direction: delta > 0 ? 1 : -1 });
    }

    galleryPrevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      galleryStep(-1);
    });
    galleryNextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      galleryStep(1);
    });

    // Swipe navigation on the inline gallery (touch + mouse/pen drag).
    // A real horizontal swipe steps the gallery; its trailing click (which
    // would otherwise open the lightbox) is suppressed.
    if (mainBtn && images.length > 1) {
      const SWIPE_MIN = 40;
      let startX = 0, startY = 0, dx = 0, dragging = false, horizontal = false, swiped = false;

      const swStart = (x, y) => {
        startX = x; startY = y; dx = 0;
        dragging = true; horizontal = false; swiped = false;
      };
      const swMove = (x, y, e) => {
        if (!dragging) return;
        dx = x - startX;
        const dy = y - startY;
        if (!horizontal && Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (!horizontal) horizontal = Math.abs(dx) > Math.abs(dy);
        if (horizontal && e && e.cancelable) e.preventDefault();
      };
      const swEnd = () => {
        if (!dragging) return;
        dragging = false;
        if (!horizontal) return;
        // Ignore the swipe if a slide is mid-animation, otherwise the slide is
        // interrupted and resetGalleryTrack() flashes the previous image.
        if (galleryAnimating) { if (Math.abs(dx) >= SWIPE_MIN) swiped = true; return; }
        // Non-circular at the edges so swiping never wraps to the far image
        // (which reads as a flash of the last/first image before the new one).
        if (dx <= -SWIPE_MIN && currentIndex < images.length - 1) {
          swiped = true;
          setActiveIndex(currentIndex + 1, { animate: true, direction: 1 });
        } else if (dx >= SWIPE_MIN && currentIndex > 0) {
          swiped = true;
          setActiveIndex(currentIndex - 1, { animate: true, direction: -1 });
        }
      };

      mainBtn.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        swStart(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: true });
      mainBtn.addEventListener('touchmove', (e) => {
        swMove(e.touches[0].clientX, e.touches[0].clientY, e);
      }, { passive: false });
      mainBtn.addEventListener('touchend', swEnd);
      mainBtn.addEventListener('touchcancel', swEnd);

      if (window.PointerEvent) {
        mainBtn.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch') return;
          swStart(e.clientX, e.clientY);
        });
        mainBtn.addEventListener('pointermove', (e) => {
          if (e.pointerType === 'touch') return;
          swMove(e.clientX, e.clientY, e);
        });
        mainBtn.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'touch') return;
          swEnd();
        });
        mainBtn.addEventListener('pointercancel', (e) => {
          if (e.pointerType === 'touch') return;
          swEnd();
        });
      }

      // Suppress the lightbox-open click that follows a swipe (capture phase,
      // runs before the click listener that opens the lightbox).
      mainBtn.addEventListener('click', (e) => {
        if (swiped) {
          e.preventDefault();
          e.stopPropagation();
          swiped = false;
        }
      }, true);
    }

    // ===== Lightbox =====
    const lightbox = document.querySelector('[data-pp-lightbox]');
    if (mainBtn && lightbox) {
      const lbImage = lightbox.querySelector('[data-pp-lb-image]');
      const lbDots = lightbox.querySelector('[data-pp-lb-dots]');
      const lbPrev = lightbox.querySelector('[data-pp-lb-prev]');
      const lbNext = lightbox.querySelector('[data-pp-lb-next]');
      const lbClose = lightbox.querySelector('[data-pp-lb-close]');
      let lbIndex = 0;

      function openLightbox(at) {
        if (!images.length) return;
        lbIndex = Math.max(0, Math.min(images.length - 1, at));
        showLb();
        lbImage.style.opacity = '1';
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }

      function closeLightbox() {
        lightbox.hidden = true;
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }

      function showLb() {
        const img = images[lbIndex];
        if (!img) return;
        lbImage.style.transition = 'none';
        lbImage.style.transform = 'translateX(0)';
        lbImage.src = img.src;
        lbImage.alt = img.alt;
        updateLbDots();
      }

      function buildLbDots() {
        if (!lbDots || images.length < 2) return;
        lbDots.innerHTML = '';
        images.forEach((img, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'pp__dot';
          b.dataset.index = String(i);
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', `Bild ${i + 1}`);
          lbDots.appendChild(b);
        });
        updateLbDots();
      }

      function updateLbDots() {
        if (!lbDots) return;
        lbDots.querySelectorAll('.pp__dot').forEach((d, i) => {
          const active = i === lbIndex;
          d.classList.toggle('is-active', active);
          d.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }

      buildLbDots();

      lbDots?.addEventListener('click', (e) => {
        const dot = e.target.closest('.pp__dot');
        if (!dot) return;
        const i = parseInt(dot.dataset.index, 10);
        if (!Number.isNaN(i)) {
          lbIndex = i;
          showLb();
        }
      });

      mainBtn.addEventListener('click', () => openLightbox(currentIndex));
      lbClose?.addEventListener('click', closeLightbox);
      lbPrev?.addEventListener('click', (ev) => {
        ev.stopPropagation();
        lbIndex = (lbIndex - 1 + images.length) % images.length;
        showLb();
      });
      lbNext?.addEventListener('click', (ev) => {
        ev.stopPropagation();
        lbIndex = (lbIndex + 1) % images.length;
        showLb();
      });

      // Close on click anywhere outside the image / arrows / close button
      lightbox.addEventListener('click', (e) => {
        if (e.target.closest('[data-pp-lb-image], [data-pp-lb-prev], [data-pp-lb-next], [data-pp-lb-close]')) return;
        closeLightbox();
      });

      // Keyboard nav
      document.addEventListener('keydown', (e) => {
        if (lightbox.hidden) return;
        if (e.key === 'Escape') closeLightbox();
        else if (e.key === 'ArrowLeft') {
          lbIndex = (lbIndex - 1 + images.length) % images.length;
          showLb();
        } else if (e.key === 'ArrowRight') {
          lbIndex = (lbIndex + 1) % images.length;
          showLb();
        }
      });

      // Touch swipe navigation (mobile): follow the finger, then slide out + fade in
      const lbStage = lightbox.querySelector('.pp-lightbox__stage');
      if (lbStage) {
        const SWIPE_MIN = 50;
        let sx = 0, sy = 0, dx = 0, axis = null, swiping = false;

        function lbGo(delta) {
          lbIndex = (lbIndex + delta + images.length) % images.length;
          showLb();
        }

        lbStage.addEventListener(
          'touchstart',
          (e) => {
            if (e.touches.length !== 1) return;
            swiping = true;
            axis = null;
            dx = 0;
            sx = e.touches[0].clientX;
            sy = e.touches[0].clientY;
            lbImage.style.transition = 'none';
          },
          { passive: true }
        );

        lbStage.addEventListener(
          'touchmove',
          (e) => {
            if (!swiping) return;
            dx = e.touches[0].clientX - sx;
            const dy = e.touches[0].clientY - sy;
            if (axis === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
              axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
            }
            if (axis === 'x') {
              e.preventDefault(); // keep the page from scrolling while swiping
              lbImage.style.transform = `translateX(${dx}px)`;
              const w = lbStage.clientWidth || 1;
              lbImage.style.opacity = String(Math.max(0.3, 1 - Math.abs(dx) / w));
            }
          },
          { passive: false }
        );

        function endSwipe() {
          if (!swiping) return;
          swiping = false;
          const w = lbStage.clientWidth || 320;
          if (axis === 'x' && Math.abs(dx) > SWIPE_MIN && images.length > 1) {
            const goNext = dx < 0; // swipe left -> next
            lbImage.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
            lbImage.style.transform = `translateX(${goNext ? -w : w}px)`;
            lbImage.style.opacity = '0';
            window.setTimeout(() => {
              lbGo(goNext ? 1 : -1); // swaps src + recentres instantly (transition: none)
              void lbImage.offsetWidth; // reflow so the fade-in animates
              lbImage.style.transition = 'opacity 0.2s ease';
              lbImage.style.opacity = '1';
            }, 180);
          } else {
            // not far enough / vertical: snap back
            lbImage.style.transition = 'transform 0.18s ease, opacity 0.18s ease';
            lbImage.style.transform = 'translateX(0)';
            lbImage.style.opacity = '1';
          }
        }

        lbStage.addEventListener('touchend', endSwipe);
        lbStage.addEventListener('touchcancel', endSwipe);
      }
    }

    // Variant pills
    if (variantsRoot) {
      variantsRoot.addEventListener(
        'mousedown',
        (e) => {
          const btn = e.target.closest('.pp-variant');
          if (!btn || btn.disabled || btn.classList.contains('is-unavailable')) return;
          try {
            btn.focus({ preventScroll: true });
          } catch (err) {
            btn.focus();
          }
        },
        true
      );
      variantsRoot.addEventListener('click', (e) => {
        const btn = e.target.closest('.pp-variant');
        if (!btn || btn.disabled || btn.classList.contains('is-unavailable')) return;
        selectVariant(btn);
      });
    }

    function selectVariant(btn) {
      variantsRoot.querySelectorAll('.pp-variant').forEach((other) => {
        other.classList.remove('is-selected');
        other.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');

      const variantId = btn.getAttribute('data-variant-id');
      const price = btn.getAttribute('data-variant-price');
      const comparePrice = btn.getAttribute('data-variant-compare-price');
      const unitPrice = btn.getAttribute('data-variant-unit-price');
      const available = btn.getAttribute('data-variant-available') === 'true';
      const image = btn.getAttribute('data-variant-image');
      const sku = btn.getAttribute('data-variant-sku');

      if (variantIdInput && variantId) variantIdInput.value = variantId;
      if (priceEl && price) priceEl.textContent = price;

      const compareEl = section.querySelector('[data-pp-compare]');
      if (compareEl) {
        if (comparePrice) {
          compareEl.textContent = comparePrice;
          compareEl.classList.remove('pp__compare--empty');
          compareEl.setAttribute('aria-hidden', 'false');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('pp__compare--empty');
          compareEl.setAttribute('aria-hidden', 'true');
        }
      }

      const unitPriceEl2 = section.querySelector('[data-pp-unit-price]');
      if (unitPriceEl2) unitPriceEl2.textContent = unitPrice ? `(${unitPrice})` : '';

      const skuEl = section.querySelector('[data-pp-sku]');
      if (skuEl && sku) skuEl.textContent = sku;

      if (mainImage && image) {
        mainImage.src = image;
        mainImage.srcset = '';
      }
      if (addBtn) {
        addBtn.disabled = !available;
        if (addLabel) addLabel.textContent = available ? 'IN DEN WARENKORB' : 'AUSVERKAUFT';
      }

      // Keep the wishlist heart pointed at the selected variant
      const favBtn = section.querySelector('favorite-button');
      if (favBtn) {
        if (variantId) favBtn.dataset.variantId = variantId;
        if (price) favBtn.dataset.price = price;
        favBtn.dataset.compareAtPrice = comparePrice || '';
        if (image) favBtn.dataset.image = image;
        if (sku) favBtn.dataset.sku = sku;
        favBtn.dataset.available = String(available);
        const vLabel = btn.querySelector('.pp-variant__label');
        if (vLabel) favBtn.dataset.variantTitle = vLabel.textContent.trim();
        if (typeof favBtn.update === 'function') favBtn.update();
      }

      // Update URL with variant param
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url);
    }

    // Qty +/-
    section.querySelectorAll('[data-pp-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = btn.getAttribute('data-pp-qty');
        const current = parseInt(qtyInput.value, 10) || 1;
        const next = dir === 'plus' ? current + 1 : Math.max(1, current - 1);
        qtyInput.value = String(next);
      });
    });

    // AJAX add to cart
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (addBtn.disabled) return;

        const originalLabel = addLabel.textContent;
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
        addLabel.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.adding) || originalLabel;

        try {
          const formData = new FormData(form);
          const variantId = formData.get('id');
          const qtyVal = Number(formData.get('quantity')) || 1;
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: qtyVal }] }),
          });
          if (res.status === 422) {
            addLabel.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.limit_reached) || originalLabel;
            setTimeout(() => {
              addLabel.textContent = originalLabel;
              addBtn.classList.remove('is-loading');
              addBtn.disabled = false;
            }, 2000);
            return;
          }
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.description || err.message || 'Fehler beim Hinzufügen');
          }
          const qty = Number(formData.get('quantity')) || 1;
          const productId = section.getAttribute('data-product-id') || '';
          // Match theme events.js (CartAddEvent) + cart-icon.js: detail.data.itemCount + source 'product-form-component' increments the bubble
          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: null,
                sourceId: 'palais-product',
                data: {
                  source: 'product-form-component',
                  itemCount: qty,
                  productId: productId,
                },
              },
            })
          );
          addLabel.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.added) || originalLabel;
          setTimeout(() => {
            addLabel.textContent = originalLabel;
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
          }, 1500);
        } catch (err) {
          console.error('[palais-product] add failed:', err);
          addLabel.textContent = 'Fehler';
          setTimeout(() => {
            addLabel.textContent = originalLabel;
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
          }, 1600);
        }
      });
    }

    // Product recommendations (lazy-load via Shopify Section Rendering API; avoid <product-recommendations> CE — needs id + data-product-id)
    const reco = section.querySelector('[data-pp-reco-fetch][data-url]');
    if (reco) {
      const grid = reco.querySelector('[data-pp-reco-grid]');
      if (grid && grid.children.length === 0) {
        loadRecommendations(reco, grid);
      }
    }

    if (!section.dataset.ppRecoCarouselDelegate) {
      section.dataset.ppRecoCarouselDelegate = 'true';
      section.addEventListener('click', onRecoCarouselClick);
    }
    setupRecoCarousel(section);

    setupRecoCart(section);
  }

  function setupRecoCart(section) {
    if (section.dataset.ppRecoBound === 'true') return;
    section.dataset.ppRecoBound = 'true';

    section.querySelectorAll('[data-pp-reco-card]').forEach((card) => updateRecoNavStates(card, { scroll: false }));

    section.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('[data-pp-reco-prev]');
      if (prevBtn && section.contains(prevBtn)) {
        e.preventDefault();
        const card = prevBtn.closest('[data-pp-reco-card]');
        if (card && !prevBtn.disabled) selectAdjacentRecoVariant(card, -1);
        return;
      }
      const nextBtn = e.target.closest('[data-pp-reco-next]');
      if (nextBtn && section.contains(nextBtn)) {
        e.preventDefault();
        const card = nextBtn.closest('[data-pp-reco-card]');
        if (card && !nextBtn.disabled) selectAdjacentRecoVariant(card, 1);
        return;
      }
      const vBtn = e.target.closest('[data-pp-reco-variant]');
      if (vBtn && section.contains(vBtn)) {
        e.preventDefault();
        if (vBtn.disabled) return;
        handleRecoVariantSelect(vBtn);
        return;
      }
      const addBtn = e.target.closest('[data-pp-reco-add]');
      if (addBtn && section.contains(addBtn)) {
        e.preventDefault();
        handleRecoAddToCart(addBtn);
      }
    });
  }

  function selectAdjacentRecoVariant(card, delta) {
    const buttons = Array.from(card.querySelectorAll('[data-pp-reco-variant]'));
    let idx = buttons.findIndex((b) => b.classList.contains('is-selected'));
    if (idx < 0) idx = 0;
    const target = idx + delta;
    if (target < 0 || target >= buttons.length) return;
    handleRecoVariantSelect(buttons[target]);
  }

  function updateRecoNavStates(card, opts) {
    const scroll = opts?.scroll !== false;
    const prevSlot = card.querySelector('[data-pp-reco-nav-slot="prev"]');
    const nextSlot = card.querySelector('[data-pp-reco-nav-slot="next"]');
    const prev = card.querySelector('[data-pp-reco-prev]');
    const next = card.querySelector('[data-pp-reco-next]');
    const buttons = Array.from(card.querySelectorAll('[data-pp-reco-variant]'));
    if (!prevSlot || !nextSlot || !prev || !next || buttons.length === 0) return;
    const idx = buttons.findIndex((b) => b.classList.contains('is-selected'));
    const i = idx >= 0 ? idx : 0;
    const atFirst = i <= 0;
    const atLast = i >= buttons.length - 1;
    prevSlot.classList.toggle('pp__reco-nav-slot--collapsed', atFirst);
    nextSlot.classList.toggle('pp__reco-nav-slot--collapsed', atLast);
    prev.disabled = atFirst;
    prev.setAttribute('aria-hidden', atFirst ? 'true' : 'false');
    if (atFirst) prev.setAttribute('tabindex', '-1');
    else prev.removeAttribute('tabindex');
    next.disabled = atLast;
    next.setAttribute('aria-hidden', atLast ? 'true' : 'false');
    if (atLast) next.setAttribute('tabindex', '-1');
    else next.removeAttribute('tabindex');
    const selected = buttons[i];
    if (selected && scroll) {
      selected.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }

  function handleRecoVariantSelect(btn) {
    const card = btn.closest('[data-pp-reco-card]');
    if (!card) return;
    card.querySelectorAll('[data-pp-reco-variant]').forEach((b) => {
      const on = b === btn;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    const priceEl = card.querySelector('[data-pp-reco-price]');
    const price = btn.getAttribute('data-variant-price');
    if (priceEl && price) priceEl.textContent = price;
    const priceCents = parseInt(btn.getAttribute('data-variant-price-cents') || '0', 10) || 0;
    const compareCents = parseInt(btn.getAttribute('data-variant-compare-at-cents') || '0', 10) || 0;
    const compareFormatted = btn.getAttribute('data-variant-compare-formatted') || '';
    const onSale = compareCents > priceCents && priceCents > 0;
    const priceOldEl = card.querySelector('[data-pp-reco-price-old]');
    if (priceOldEl) {
      if (onSale && compareFormatted) {
        priceOldEl.textContent = compareFormatted;
        priceOldEl.hidden = false;
      } else {
        priceOldEl.textContent = '';
        priceOldEl.hidden = true;
      }
    }
    const discountWrap = card.querySelector('[data-pp-reco-discount-wrap]');
    const discountEl = card.querySelector('[data-pp-reco-discount]');
    if (discountWrap && discountEl) {
      if (onSale && compareCents > 0) {
        const pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
        discountEl.textContent = `-${pct}%`;
        discountWrap.hidden = false;
      } else {
        discountEl.textContent = '';
        discountWrap.hidden = true;
      }
    }
    const addBtn = card.querySelector('[data-pp-reco-add]');
    if (addBtn) {
      const vid = btn.getAttribute('data-variant-id');
      if (vid) addBtn.dataset.variantId = vid;
      addBtn.disabled = btn.getAttribute('data-variant-available') !== 'true';
    }
    updateRecoNavStates(card);
  }

  async function handleRecoAddToCart(btn) {
    if (btn.disabled) return;
    const vid = btn.dataset.variantId;
    if (!vid) return;
    const card = btn.closest('[data-pp-reco-card]');
    const productId = card?.getAttribute('data-product-id') || '';
    const original = btn.textContent.trim();
    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.adding) || original;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
      });
      if (res.status === 422) {
        btn.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.limit_reached) || original;
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }, 2000);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Fehler beim Hinzufügen');
      }
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'pp-reco',
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId: productId,
            },
          },
        })
      );
      btn.textContent = (window.PDT_PP_TEXTS && window.PDT_PP_TEXTS.added) || original;
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }, 1500);
    } catch (err) {
      console.error('[palais-product] reco add failed:', err);
      btn.textContent = 'Fehler';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }, 1600);
    }
  }

  async function loadRecommendations(host, grid) {
    const url = host.getAttribute('data-url');
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      let newGrid = tmp.querySelector('[data-pp-reco-grid]');
      if (!newGrid) {
        const sec = tmp.querySelector('[id^="shopify-section-pp-reco"]');
        if (sec) newGrid = sec.querySelector('[data-pp-reco-grid]');
      }
      if (newGrid && newGrid.children.length > 0) {
        grid.replaceChildren(...Array.from(newGrid.children));
        grid.querySelectorAll('[data-pp-reco-card]').forEach((card) => updateRecoNavStates(card, { scroll: false }));
        const sectionEl = grid.closest('.pp[data-product-id]');
        if (sectionEl) setupRecoCarousel(sectionEl);
      }
    } catch (e) {
      console.warn('[palais-product] recommendations failed:', e);
    }
  }

  function initAll() {
    document.querySelectorAll('.pp[data-product-id]').forEach((section) => {
      if (section.dataset.ppInit === 'true') return;
      section.dataset.ppInit = 'true';
      init(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
